import { useState, useMemo } from 'react';
import {
  Box,
  Heading,
  HStack,
  Select,
  Text,
  VStack,
  useColorModeValue,
} from '@chakra-ui/react';
import { Link } from 'react-router-dom';
import { useAllArbitration } from '../../hooks/useAdmin';
import { LoadingSpinner } from '../common/LoadingSpinner';
import { EmptyState } from '../common/EmptyState';
import { StatusBadge } from '../common/StatusBadge';
import { formatRelativeTime } from '../../utils/formatters';
import { FiAlertTriangle } from 'react-icons/fi';

export default function AdminDisputes() {
  const { cases, loading } = useAllArbitration();
  const [statusFilter, setStatusFilter] = useState('open');

  const bg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.100', 'gray.700');

  const filtered = useMemo(() => {
    if (statusFilter === 'all') return cases;
    return cases.filter((c) => c.status === statusFilter);
  }, [cases, statusFilter]);

  if (loading) return <LoadingSpinner message="Loading disputes..." />;

  return (
    <VStack spacing={6} align="stretch">
      <HStack justify="space-between" align="center">
        <Box>
          <Heading size="lg">Disputes</Heading>
          <Text color="gray.500" mt={1}>{cases.length} total disputes</Text>
        </Box>
        <Select maxW="180px" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="all">All</option>
          <option value="open">Open</option>
          <option value="resolved">Resolved</option>
        </Select>
      </HStack>

      {filtered.length === 0 ? (
        <EmptyState
          title={statusFilter === 'open' ? 'No open disputes' : 'No disputes found'}
          description="Disputes will appear here when users file them."
          icon={FiAlertTriangle}
        />
      ) : (
        <VStack spacing={3} align="stretch">
          {filtered.map((c) => (
            <Box
              key={c.id}
              as={Link}
              to={`/admin/disputes/${c.id}`}
              bg={bg}
              rounded="lg"
              borderWidth={1}
              borderColor={borderColor}
              p={4}
              _hover={{ borderColor: 'orange.300', shadow: 'sm' }}
              transition="all 0.2s"
              display="block"
            >
              <HStack justify="space-between" align="start" mb={2}>
                <Text fontWeight="semibold" fontSize="md" noOfLines={1} flex={1}>
                  Dispute: {c.reason?.slice(0, 80) || 'No reason given'}{c.reason?.length > 80 ? '...' : ''}
                </Text>
                <StatusBadge status={c.status} />
              </HStack>
              <HStack spacing={4} fontSize="sm" color="gray.500">
                <Text>Task: {c.bountyId?.slice(0, 8)}...</Text>
                <Text>Submission: {c.claimId?.slice(0, 8)}...</Text>
              </HStack>
              <Text fontSize="xs" color="gray.400" mt={1}>
                {formatRelativeTime(c.initiatedAt)}
              </Text>
            </Box>
          ))}
        </VStack>
      )}
    </VStack>
  );
}
